import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, documentTextButtonSx } from '../constants/documentTheme';
import PdfPreview from './PdfPreview';

/**
 * Shows a trip document inline. The file arrives as an object URL already
 * fetched with the auth header, since the download endpoint is not public.
 */
const DocumentPreviewDialog = ({ preview, onClose, onDownload }) => {
  const { t } = useTranslation();
  const open = Boolean(preview);
  const { document: document_, url, kind } = preview || {};

  const renderBody = () => {
    if (!document_) return null;

    if (kind === 'image') {
      return (
        <Box
          component="img"
          src={url}
          alt={document_.title}
          data-testid="document-preview-image"
          sx={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', mx: 'auto', objectFit: 'contain' }}
        />
      );
    }

    if (kind === 'pdf') {
      return <PdfPreview url={url} title={document_.title} />;
    }

    if (kind === 'text') {
      return (
        <Box
          component="iframe"
          src={url}
          title={document_.title}
          data-testid="document-preview-frame"
          sandbox=""
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: 'calc(100vh - 140px)', sm: '75vh' },
            border: `1px solid ${DOCUMENT.rule}`,
            bgcolor: '#fff',
          }}
        />
      );
    }

    return (
      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, py: 2 }}>
        {t('documents.previewUnavailable')}
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableScrollLock
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 0, sm: 4 },
          width: { xs: '100%', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
          height: { xs: '100%', sm: 'auto' },
          borderRadius: { xs: 0, sm: 1 },
        },
      }}
    >
      <DialogTitle sx={{ pr: 2, overflowWrap: 'anywhere' }}>
        {document_?.title}
        {document_?.file_name && (
          <Typography component="span" sx={{ display: 'block', fontSize: 12.5, color: DOCUMENT.ink3 }}>
            {document_.file_name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>{renderBody()}</DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        {kind && (
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={documentTextButtonSx}
          >
            {t('documents.openInNewTab')}
          </Button>
        )}
        <Button size="small" onClick={onDownload} sx={documentTextButtonSx}>
          {t('documents.download')}
        </Button>
        <Button size="small" onClick={onClose} sx={documentTextButtonSx}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
